import { supabase } from "@/utils/supabase/client";
import { debounce } from "lodash";

/**
 * Standard widget update handler that updates the draft and returns formatted data
 * @param {string} widgetId - The ID of the widget to update
 * @param {Object} currentData - Current widget data (config or layout)
 * @param {Object} updates - Updates to apply
 * @param {Function} onUpdate - Callback function to update the UI
 * @returns {Promise<Object>} The updated widget data
 */
export const updateWidgetInDatabase = async (
  widgetId,
  currentData,
  updates
) => {
  try {
    // Update the database with the provided updates
    const { data, error } = await supabase
      .from("space_widget_drafts")
      .update({
        ...updates,
        last_autosave_at: new Date().toISOString(),
      })
      .eq("id", widgetId)
      .select()
      .single();

    if (error) {
      throw error;
    }

    return data;
  } catch (error) {
    console.error("Error updating widget:", error);
    throw error;
  }
};

/**
 * Updates a widget's configuration in Supabase space_widget_drafts table
 * @param {string} widgetId - The ID of the widget to update
 * @param {Object} updates - The updates to apply to the widget's config
 * @returns {Promise<Object>} The updated widget data
 */
export async function updateWidgetInSupabase(widgetId, updates) {
  try {
    const { data, error } = await supabase
      .from("space_widget_drafts")
      .update({
        config: updates,
        last_autosave_at: new Date().toISOString(),
      })
      .eq("id", widgetId)
      .select()
      .single();

    console.log("data", data);

    if (error) throw error;
    return data;
  } catch (error) {
    console.error("Error updating widget draft:", error);
    throw error;
  }
}

/**
 * Calculates the dimensions for a widget based on its size and the grid configuration
 * @param {string} size - The size variant of the widget ('mini', 'wide', 'tall', 'super-wide', 'standard')
 * @param {number} containerWidth - The total width of the container
 * @param {number} gapPixels - The size of the gap between widgets in pixels
 * @param {number} totalColumns - The total number of columns in the grid
 * @returns {Object} The width and height in pixels, and the number of columns and rows the widget spans
 */
export function getWidgetDimensions(
  size = "standard",
  containerWidth,
  gapPixels = 24,
  totalColumns = 12
) {
  // Calculate the width of a single column (accounting for gaps)
  const totalGapWidth = gapPixels * (totalColumns - 1);
  const usableWidth = containerWidth - totalGapWidth;
  const columnWidth = usableWidth / totalColumns;

  // Define the column and row spans for each size
  let cols, rows;
  switch (size) {
    case "mini":
      cols = 2;
      rows = 2;
      break;
    case "wide":
      cols = 4;
      rows = 2;
      break;
    case "tall":
      cols = 2;
      rows = 4;
      break;
    case "super-wide":
      cols = 8;
      rows = 4;
      break;
    case "standard":
    default:
      cols = 4;
      rows = 4;
  }

  // Calculate actual pixel dimensions
  // Width includes the gaps within the widget's columns
  const widthPx = cols * columnWidth + (cols - 1) * gapPixels;

  // For height, we want to maintain aspect ratio based on the total width
  // If width is 400px and we want 2:1, height should be 200px minus half a gap on top and bottom
  const baseHeight = widthPx * (rows / cols); // This maintains the aspect ratio based on the total width
  const heightPx = baseHeight - gapPixels; // Subtract one gap (half from top, half from bottom)

  return {
    width: widthPx,
    height: heightPx,
    columns: cols,
    rows: rows,
  };
}

/**
 * Utility function to get a widget's position consistently across app components
 * @param {Object} widget - The widget object
 * @returns {number} - The position value to use for sorting
 */
export const getWidgetPosition = (widget) => {
  // Use desktop position as primary sorting key
  const desktopPos = widget.layout?.desktop?.position;
  if (typeof desktopPos === "number") return desktopPos;

  // Fall back to tablet and mobile if desktop not available
  const tabletPos = widget.layout?.tablet?.position;
  if (typeof tabletPos === "number") return tabletPos;

  const mobilePos = widget.layout?.mobile?.position;
  if (typeof mobilePos === "number") return mobilePos;

  // Last resort fallback
  return widget.layout?.desktop_order || 0;
};

/**
 * Get a widget's position for a specific viewport/layout
 * @param {Object} widget - The widget object
 * @param {string} viewport - The viewport to get position for ('desktop', 'tablet', 'mobile')
 * @returns {number} - The position value for the specified viewport
 */
export const getWidgetViewportPosition = (widget, viewport = "desktop") => {
  // Try to get position for the specified viewport
  const viewportPos = widget.layout?.[viewport]?.position;
  if (typeof viewportPos === "number") return viewportPos;

  // Fall back based on device size
  if (viewport === "mobile") {
    // Mobile falls back to tablet then desktop
    const tabletPos = widget.layout?.tablet?.position;
    if (typeof tabletPos === "number") return tabletPos;

    const desktopPos = widget.layout?.desktop?.position;
    if (typeof desktopPos === "number") return desktopPos;
  } else if (viewport === "tablet") {
    // Tablet falls back to desktop
    const desktopPos = widget.layout?.desktop?.position;
    if (typeof desktopPos === "number") return desktopPos;
  }

  // Last resort
  return widget.layout?.desktop_order || 0;
};

/**
 * Sort widgets based on their position
 * @param {Array} widgets - Array of widget objects
 * @param {string} viewport - Optional viewport to sort by ('desktop', 'tablet', 'mobile')
 * @returns {Array} - Sorted array of widgets
 */
export const sortWidgetsByPosition = (widgets, viewport) => {
  if (!widgets || !Array.isArray(widgets)) return [];

  if (viewport) {
    // Sort by specific viewport if provided
    return [...widgets].sort(
      (a, b) =>
        getWidgetViewportPosition(a, viewport) -
        getWidgetViewportPosition(b, viewport)
    );
  }

  // Default sort using all viewports
  return [...widgets].sort(
    (a, b) => getWidgetPosition(a) - getWidgetPosition(b)
  );
};

// Widget state management utility functions

/**
 * Synchronizes widget state after undo/redo operations
 * @param {Object} newState - The state to synchronize to
 * @param {string} actionType - Type of action (UNDO or REDO)
 * @param {Function} setSpaceData - State setter for space data
 * @param {Function} queueAction - Function to queue updates for saving
 * @param {Object} spaceData - Current space data
 * @param {Object} masonryRef - Reference to masonry layout component
 * @returns {boolean} - Whether synchronization was successful
 */
export const synchronizeWidgetState = (
  newState,
  actionType,
  setSpaceData,
  queueAction,
  spaceData,
  masonryRef
) => {
  if (!newState) {
    console.warn(`❌ [Sync] No valid state for ${actionType} operation`);
    return false;
  }

  const stateData = newState.data;
  console.log(`🔄 [Sync] Starting ${actionType}:`, {
    hasWidgets: !!stateData.widgets,
    widgetCount: stateData.widgets?.length,
    hasSpaceData: !!stateData.space_data,
  });

  // Special logging for widget deletion undo/redo and space changes
  if (actionType.includes("UNDO") || actionType.includes("REDO")) {
    const widgetIds = stateData.widgets?.map((w) => w.id) || [];
    console.log(`🔄 [Sync] ${actionType} widget IDs:`, widgetIds);

    if (stateData.space_data) {
      console.log(
        `🔄 [Sync] ${actionType} space data keys:`,
        Object.keys(stateData.space_data)
      );
    }
  }

  // Process both widgets and space_data if both are present
  let hasWidgets = !!stateData.widgets;
  let hasSpaceData = !!stateData.space_data;
  let newWidgets = null;

  if (hasWidgets) {
    console.log(
      `🔄 [Sync] Widget positions being applied:`,
      stateData.widgets.map((w) => ({
        id: w.id,
        position: w.layout?.desktop?.position,
        startColumn: w.layout?.desktop?.startColumn,
      }))
    );

    // Create completely new widget objects to force React re-render
    newWidgets = stateData.widgets.map((widget) => ({
      ...widget,
      layout: JSON.parse(JSON.stringify(widget.layout || {})),
      config: JSON.parse(JSON.stringify(widget.config || {})),
      // Force new reference with unique key
      _updateKey: `${actionType}_${Date.now()}_${Math.random()}`,
    }));
  }

  if (hasSpaceData) {
    console.log(`🔄 [Sync] Synchronizing space data changes:`, {
      hasTheme: !!stateData.space_data.theme,
      hasTitle: !!stateData.space_data.title,
      hasDescription: !!stateData.space_data.description,
      keys: Object.keys(stateData.space_data),
    });
  }

  // Update state with both widgets and space_data changes
  if (hasWidgets || hasSpaceData) {
    setSpaceData((prevState) => {
      if (hasWidgets) {
        console.log(
          `🔄 [Sync] Updating state: ${prevState.widgets?.length} → ${newWidgets.length} widgets`
        );
      }

      const updatedState = {
        ...prevState,
        // Force component re-render with timestamp
        _lastUpdate: Date.now(),
      };

      // Update widgets if present
      if (hasWidgets && newWidgets) {
        updatedState.widgets = newWidgets;
      }

      // Update space_data if present
      if (hasSpaceData) {
        updatedState.space_data = {
          ...prevState.space_data,
          ...stateData.space_data,
        };
      }

      return updatedState;
    });

    // Prepare updates for saving
    const updatePayload = {};

    if (hasWidgets && newWidgets) {
      // Prepare widget updates for saving
      const widgetUpdates = {};
      newWidgets.forEach((widget) => {
        widgetUpdates[widget.id] = {
          layout: widget.layout,
          config: widget.config,
          last_autosave_at: new Date().toISOString(),
        };
      });
      updatePayload.widgets = widgetUpdates;
    }

    if (hasSpaceData) {
      updatePayload.space_data = stateData.space_data;
    }

    // Queue for autosave
    if (queueAction && Object.keys(updatePayload).length > 0) {
      queueAction(
        updatePayload,
        actionType.includes("UNDO") ? "UNDO_ACTION" : "REDO_ACTION",
        hasWidgets
          ? {
              ...spaceData,
              widgets: newWidgets,
            }
          : null,
        true, // skipHistory
        true // triggerAutosave
      );
    }

    // Force layout recalculation if widgets were updated
    if (hasWidgets) {
      console.log(`🔄 [Sync] Triggering layout recalculation`);

      // Method 1: Direct ref call
      if (masonryRef?.current?.recalculateLayout) {
        setTimeout(() => {
          console.log(`🔄 [Sync] Method 1: Direct ref recalculation`);
          masonryRef.current.recalculateLayout();
        }, 10);
      }

      // Method 2: DOM-based call
      setTimeout(() => {
        const masonryContainer = document.querySelector(
          "[data-masonry-container]"
        );
        if (masonryContainer?.__ref?.recalculateLayout) {
          console.log(`🔄 [Sync] Method 2: DOM ref recalculation`);
          masonryContainer.__ref.recalculateLayout();
        }
      }, 50);

      // Method 3: Force window resize to trigger responsive recalculation
      setTimeout(() => {
        console.log(`🔄 [Sync] Method 3: Resize event dispatch`);
        window.dispatchEvent(new Event("resize"));
      }, 100);

      // Method 4: Custom event for masonry recalculation
      setTimeout(() => {
        console.log(`🔄 [Sync] Method 4: Custom masonry event`);
        const masonryContainer = document.querySelector(
          "[data-masonry-container]"
        );
        if (masonryContainer) {
          masonryContainer.dispatchEvent(
            new CustomEvent("recalculate-masonry")
          );
        }
      }, 150);
    }

    console.log(
      `✅ [Sync] State synchronized successfully - widgets: ${hasWidgets}, space_data: ${hasSpaceData}`
    );
    return true;
  }

  console.warn(`❌ [Sync] No recognizable state data to restore`);
  return false;
};

/**
 * Updates a single widget or multiple widgets
 * @param {string|null} widgetId - ID of widget to update, or null for bulk updates
 * @param {Object} changes - Changes to apply to the widget(s)
 * @param {Function} setSpaceData - State setter for space data
 * @param {Function} pushAction - Function to add action to history
 * @param {Function} queueAction - Function to queue updates for saving
 */
export const handleWidgetUpdate = (
  widgetId,
  changes,
  setSpaceData,
  pushAction,
  queueAction
) => {
  // Get current state before update for history tracking
  let previousState = null;
  setSpaceData((prevData) => {
    previousState = {
      widgets: prevData.widgets.map((w) => ({
        ...w,
        layout: { ...w.layout },
        config: { ...w.config },
      })),
    };
    return prevData;
  });

  // Handle mobile layout specific updates with special care
  if (
    changes.type === "MOBILE_LAYOUT_FIX" &&
    changes.widgets &&
    changes.ensureMobileLayout
  ) {
    console.log("Processing MOBILE_LAYOUT_FIX with special handling");

    let newState = null;

    setSpaceData((prevData) => {
      // Map through the new widgets and ensure mobile layouts are properly updated
      const updatedWidgets = changes.widgets.map((updatedWidget) => {
        // Find the original widget to compare and update
        const originalWidget = prevData.widgets.find(
          (w) => w.id === updatedWidget.id
        );
        if (!originalWidget) return updatedWidget;

        // Log the specific mobile layout changes
        if (
          updatedWidget.layout?.mobile?.position !==
          originalWidget.layout?.mobile?.position
        ) {
          console.log(`Widget ${updatedWidget.id} mobile position changed:`, {
            from: originalWidget.layout?.mobile?.position,
            to: updatedWidget.layout?.mobile?.position,
          });
        }

        // Return widget with updated layout but preserved config
        return {
          ...originalWidget,
          layout: {
            ...originalWidget.layout,
            ...updatedWidget.layout,
            // Explicitly ensure mobile layout is updated
            mobile: {
              ...(originalWidget.layout?.mobile || {}),
              ...(updatedWidget.layout?.mobile || {}),
            },
          },
        };
      });

      const newData = {
        ...prevData,
        widgets: updatedWidgets,
      };

      // Capture the new state for history (computed, not from React state)
      newState = {
        widgets: updatedWidgets.map((w) => ({
          ...w,
          layout: { ...w.layout },
          config: { ...w.config },
        })),
      };

      return newData;
    });

    // Create a specific payload that highlights mobile layout changes
    const mobileLayoutPayload = {};
    changes.widgets.forEach((widget) => {
      if (widget.layout?.mobile) {
        mobileLayoutPayload[widget.id] = {
          layout: {
            // Only include mobile layout to ensure it's focused on in history
            mobile: widget.layout.mobile,
          },
        };
      }
    });

    // Add to history with specific MOBILE_LAYOUT_FIX type
    if (!changes.skipHistory) {
      pushAction({
        type: "MOBILE_LAYOUT_FIX",
        payload: {
          widgets: mobileLayoutPayload,
        },
        previousState,
        newState,
      });
    }

    // Queue the update with explicit forcePersist flag
    queueAction(
      {
        widgets: changes.widgets.reduce((acc, widget) => {
          acc[widget.id] = {
            // Include the widget ID to ensure it's properly identified
            id: widget.id,
            // Include the full layout to ensure all layouts are preserved
            layout: widget.layout,
            // Set the last_autosave_at timestamp to ensure it's saved
            last_autosave_at: new Date().toISOString(),
          };
          return acc;
        }, {}),
      },
      "MOBILE_LAYOUT_FIX",
      null,
      changes.skipHistory,
      true, // Always trigger autosave
      changes.forcePersist // Pass through forcePersist
    );

    // Log confirmation that the mobile layouts will be persisted
    console.log("Mobile layouts queued for immediate persistence to database");
  }
  // Handle bulk widget updates (e.g., drag and drop)
  else if (changes.type === "UPDATE_WIDGET" && changes.widgets) {
    let newState = null;

    setSpaceData((prevData) => {
      // Map through the new widgets and preserve their existing config/content
      const updatedWidgets = changes.widgets.map((updatedWidget) => {
        // Find the original widget to preserve its config and other properties
        const originalWidget = prevData.widgets.find(
          (w) => w.id === updatedWidget.id
        );
        if (!originalWidget) return updatedWidget;

        // Return widget with updated layout but preserved config and other properties
        return {
          ...originalWidget,
          layout: updatedWidget.layout,
        };
      });

      const newData = {
        ...prevData,
        widgets: updatedWidgets,
      };

      // Capture the new state for history (computed, not from React state)
      newState = {
        widgets: updatedWidgets.map((w) => ({
          ...w,
          layout: { ...w.layout },
          config: { ...w.config },
        })),
      };

      return newData;
    });

    // Create action for history
    const action = {
      type: "UPDATE_WIDGET",
      payload: {
        widgets: changes.widgets.reduce((acc, widget) => {
          acc[widget.id] = widget;
          return acc;
        }, {}),
      },
      previousState,
      newState,
    };

    // Add to history unless skipped
    if (!changes.skipHistory) {
      pushAction(action);
    }

    // Queue the update for database save
    queueAction(
      {
        widgets: changes.widgets.reduce((acc, widget) => {
          acc[widget.id] = widget;
          return acc;
        }, {}),
      },
      "UPDATE_WIDGET",
      null,
      changes.skipHistory,
      changes.triggerAutosave !== false // Default to true unless explicitly set to false
    );
  }
  // Handle single widget update
  else if (widgetId) {
    let newState = null;

    setSpaceData((prevData) => {
      const updatedWidgets = prevData.widgets.map((widget) => {
        if (widget.id === widgetId) {
          // Preserve existing properties and merge with new changes
          return {
            ...widget,
            config: changes.config || widget.config,
            layout: changes.layout || widget.layout,
          };
        }
        return widget;
      });

      const newData = {
        ...prevData,
        widgets: updatedWidgets,
      };

      // Capture the new state for history (computed, not from React state)
      newState = {
        widgets: updatedWidgets.map((w) => ({
          ...w,
          layout: { ...w.layout },
          config: { ...w.config },
        })),
      };

      return newData;
    });

    // Determine the appropriate action type based on what was changed
    let actionType = changes.type || "CONFIG_CHANGE";
    if (!changes.type) {
      if (changes.layout && !changes.config) {
        actionType = "LAYOUT_CHANGE";
      } else if (changes.config && !changes.layout) {
        actionType = "CONFIG_CHANGE";
      } else if (changes.config && changes.layout) {
        actionType = "WIDGET_UPDATE"; // Both config and layout changed
      }
    }

    // Create action for history
    const action = {
      type: actionType,
      payload: {
        widgetId,
        changes: {
          ...changes,
        },
        widgets: {
          [widgetId]: {
            ...changes,
            id: widgetId,
          },
        },
      },
      previousState,
      newState,
    };

    // Add to history unless skipped
    if (!changes.skipHistory) {
      pushAction(action);
    }

    // Queue the update for database save ONLY if this is not a real-time update
    if (!changes.isRealTimeUpdate) {
      const shouldTriggerAutosave = changes.triggerAutosave !== false;
      console.log(
        `💾 [WidgetUtils] Queuing ${actionType} for widget ${widgetId} - triggerAutosave: ${shouldTriggerAutosave}`
      );
      console.log(`💾 [WidgetUtils] Changes object:`, changes);

      queueAction(
        {
          widgets: {
            [widgetId]: {
              ...changes,
              id: widgetId,
            },
          },
        },
        actionType,
        null,
        changes.skipHistory,
        shouldTriggerAutosave
      );
    } else {
      console.log(
        `🎨 [WidgetUtils] Real-time update for widget ${widgetId} - skipping database queue`
      );
      console.log(`🎨 [WidgetUtils] Changes object for real-time:`, changes);
    }
  }
};

/**
 * Deletes a widget with enhanced state capture for undo/redo
 * @param {string} widgetId - ID of widget to delete
 * @param {Function} setSpaceData - State setter for space data
 * @param {Function} queueAction - Function to queue updates for saving
 * @param {Function} pushAction - Function to add action to history
 */
export const handleWidgetDelete = (
  widgetId,
  setSpaceData,
  queueAction,
  pushAction
) => {
  console.log(`🗑️ [WidgetUtils] Starting widget deletion for ${widgetId}`);

  // Get current state before deletion for history tracking
  let previousState = null;
  let deletedWidget = null;

  setSpaceData((prevData) => {
    // Capture the previous state with deep cloning
    previousState = {
      widgets: prevData.widgets.map((w) => ({
        ...w,
        layout: JSON.parse(JSON.stringify(w.layout || {})),
        config: JSON.parse(JSON.stringify(w.config || {})),
        widget_type: w.widget_type ? { ...w.widget_type } : null,
      })),
      space_data: prevData.space_data ? { ...prevData.space_data } : null,
    };

    // Find the widget being deleted and capture complete state
    deletedWidget = prevData.widgets.find((w) => w.id === widgetId);

    return prevData; // Don't modify yet, just capture state
  });

  let newState = null;

  setSpaceData((prevData) => {
    const updatedWidgets = prevData.widgets.filter((w) => w.id !== widgetId);

    const newData = {
      ...prevData,
      widgets: updatedWidgets,
    };

    // Capture the new state for history with deep cloning
    newState = {
      widgets: updatedWidgets.map((w) => ({
        ...w,
        layout: JSON.parse(JSON.stringify(w.layout || {})),
        config: JSON.parse(JSON.stringify(w.config || {})),
        widget_type: w.widget_type ? { ...w.widget_type } : null,
      })),
      space_data: newData.space_data ? { ...newData.space_data } : null,
    };

    return newData;
  });

  // Record the deletion in history with complete widget data for restoration
  if (pushAction && deletedWidget) {
    console.log(
      `📝 [WidgetUtils] Recording widget deletion in history for ${widgetId}`
    );

    // Create a complete widget snapshot for restoration
    const completeWidgetSnapshot = {
      id: deletedWidget.id,
      space_id: deletedWidget.space_id,
      widget_type_id:
        deletedWidget.widget_type_id || deletedWidget.widget_type?.id,
      config: JSON.parse(JSON.stringify(deletedWidget.config || {})),
      layout: JSON.parse(JSON.stringify(deletedWidget.layout || {})),
      position: deletedWidget.layout?.desktop?.position || 0,
      // Store complete widget_type information for restoration
      widget_type: deletedWidget.widget_type
        ? {
            id: deletedWidget.widget_type.id,
            type: deletedWidget.widget_type.type,
            name: deletedWidget.widget_type.name,
            config_enabled: deletedWidget.widget_type.config_enabled,
            settings_enabled: deletedWidget.widget_type.settings_enabled,
            default_config: deletedWidget.widget_type.default_config,
            default_layout: deletedWidget.widget_type.default_layout,
            created_at: deletedWidget.widget_type.created_at,
            updated_at: deletedWidget.widget_type.updated_at,
          }
        : null,
      created_at: deletedWidget.created_at,
      updated_at: deletedWidget.updated_at,
      last_autosave_at: new Date().toISOString(),
      // Store any additional properties that might be needed
      ...(deletedWidget.draft_id && { draft_id: deletedWidget.draft_id }),
      ...(deletedWidget.published_at && {
        published_at: deletedWidget.published_at,
      }),
    };

    pushAction({
      type: "WIDGET_DELETE",
      payload: {
        widgetId,
        deletedWidget: completeWidgetSnapshot,
      },
      previousState,
      newState,
      metadata: {
        widgetType: deletedWidget.widget_type?.type || "unknown",
        deletionTimestamp: new Date().toISOString(),
        canBeRestored: true,
      },
    });
  }

  // Queue the deletion for database persistence
  // This ensures the deletion is saved to the database and can be undone if needed
  if (queueAction) {
    console.log(
      `💾 [WidgetUtils] Queuing widget ${widgetId} deletion for database persistence`
    );

    queueAction(
      {
        // Mark the widget as deleted - this is what the autosave system expects
        widgets: {
          [widgetId]: {
            id: widgetId,
            deleted: true, // This flag tells the autosave system to delete the widget
            last_autosave_at: new Date().toISOString(),
          },
        },
      },
      "WIDGET_DELETE",
      null,
      false, // Don't skip history - we already handled it above
      true // Trigger autosave to persist the deletion
    );
  }

  console.log(
    `✅ [WidgetUtils] Widget ${widgetId} deletion queued for database persistence`
  );
};

/**
 * Updates space data directly without history tracking
 * This is used for system updates (like post-publish) where we don't want
 * to track the changes as user actions in history
 * @param {Function} setSpaceData - State setter for space data
 * @param {Object|Function} updaterOrValue - New space data or updater function
 */
export const handleDirectSpaceUpdate = (setSpaceData, updaterOrValue) => {
  console.log(
    "🔄 [DirectSpaceUpdate] Updating space data without history tracking"
  );

  setSpaceData((prevData) => {
    // Apply the update
    const newData =
      typeof updaterOrValue === "function"
        ? updaterOrValue(prevData)
        : updaterOrValue;

    console.log("✅ [DirectSpaceUpdate] Space data updated directly");
    return newData;
  });
};

/**
 * Updates space data with history tracking
 * @param {Function} setSpaceData - State setter for space data
 * @param {Object|Function} updaterOrValue - New space data or updater function
 * @param {Function} pushAction - Function to add action to history
 * @param {Function} queueAction - Function to queue updates for saving
 */
export const handleSpaceUpdate = (
  setSpaceData,
  updaterOrValue,
  pushAction,
  queueAction
) => {
  // Get current state before update for history tracking
  let previousState = null;

  setSpaceData((prevData) => {
    // Capture the previous state with deep cloning
    previousState = {
      space_data: prevData.space_data
        ? JSON.parse(JSON.stringify(prevData.space_data))
        : null,
      widgets: prevData.widgets
        ? prevData.widgets.map((w) => ({
            ...w,
            layout: JSON.parse(JSON.stringify(w.layout || {})),
            config: JSON.parse(JSON.stringify(w.config || {})),
          }))
        : [],
    };

    return prevData; // Don't modify yet, just capture state
  });

  let newState = null;

  setSpaceData((prevData) => {
    // Apply the update
    const newData =
      typeof updaterOrValue === "function"
        ? updaterOrValue(prevData)
        : updaterOrValue;

    // Capture the new state for history with deep cloning
    newState = {
      space_data: newData.space_data
        ? JSON.parse(JSON.stringify(newData.space_data))
        : null,
      widgets: newData.widgets
        ? newData.widgets.map((w) => ({
            ...w,
            layout: JSON.parse(JSON.stringify(w.layout || {})),
            config: JSON.parse(JSON.stringify(w.config || {})),
          }))
        : [],
    };

    return newData;
  });

  // Determine what type of space change this is
  let actionType = "SPACE_DATA_CHANGE";
  let changeDescription = "Updated space settings";
  let spaceDataChanges = {};

  if (previousState?.space_data && newState?.space_data) {
    // Check for theme changes
    if (
      JSON.stringify(previousState.space_data.theme) !==
      JSON.stringify(newState.space_data.theme)
    ) {
      actionType = "THEME_CHANGE";
      changeDescription = "Changed theme settings";
      spaceDataChanges.theme = newState.space_data.theme;
    }
    // Check for other specific changes
    else if (previousState.space_data.title !== newState.space_data.title) {
      changeDescription = "Changed space title";
      spaceDataChanges.title = newState.space_data.title;
    } else if (
      previousState.space_data.description !== newState.space_data.description
    ) {
      changeDescription = "Changed space description";
      spaceDataChanges.description = newState.space_data.description;
    } else if (
      previousState.space_data.require_password !==
      newState.space_data.require_password
    ) {
      changeDescription = newState.space_data.require_password
        ? "Enabled password protection"
        : "Disabled password protection";
      spaceDataChanges.require_password = newState.space_data.require_password;
    } else if (
      previousState.space_data.is_published !== newState.space_data.is_published
    ) {
      changeDescription = newState.space_data.is_published
        ? "Published space"
        : "Unpublished space";
      spaceDataChanges.is_published = newState.space_data.is_published;
    } else {
      // Generic space data change - find what changed
      Object.keys(newState.space_data).forEach((key) => {
        if (
          JSON.stringify(previousState.space_data[key]) !==
          JSON.stringify(newState.space_data[key])
        ) {
          spaceDataChanges[key] = newState.space_data[key];
        }
      });
    }
  }

  // Record the space update in history
  if (pushAction) {
    console.log(
      `📝 [SpaceUtils] Recording space update in history: ${changeDescription}`
    );

    pushAction({
      type: actionType,
      payload: {
        spaceDataChanges,
        changeDescription,
      },
      previousState,
      newState,
      metadata: {
        changeType: actionType,
        changeTimestamp: new Date().toISOString(),
        canBeRestored: true,
      },
    });
  }

  // Queue space data changes for saving
  if (queueAction && Object.keys(spaceDataChanges).length > 0) {
    queueAction(
      { space_data: spaceDataChanges },
      actionType,
      null,
      false, // Don't skip history
      true // Trigger autosave
    );
  }

  console.log(`✅ [SpaceUtils] Space update completed: ${changeDescription}`);
};

/**
 * Adds a widget with history tracking
 * @param {Object} newWidget - The widget to add
 * @param {Function} setSpaceData - State setter for space data
 * @param {Function} pushAction - Function to add action to history
 * @param {Function} queueAction - Function to queue updates for saving
 */
export const handleWidgetAdd = (
  newWidget,
  setSpaceData,
  pushAction,
  queueAction
) => {
  // Get current state before addition for history tracking
  let previousState = null;

  setSpaceData((prevData) => {
    // Capture the previous state
    previousState = {
      widgets: prevData.widgets.map((w) => ({
        ...w,
        layout: { ...w.layout },
        config: { ...w.config },
      })),
    };

    return prevData; // Don't modify yet, just capture state
  });

  let newState = null;

  setSpaceData((prevData) => {
    const updatedWidgets = [...prevData.widgets, newWidget];

    const newData = {
      ...prevData,
      widgets: updatedWidgets,
    };

    // Capture the new state for history
    newState = {
      widgets: updatedWidgets.map((w) => ({
        ...w,
        layout: { ...w.layout },
        config: { ...w.config },
      })),
    };

    return newData;
  });

  // Record the addition in history with complete widget information for database operations
  if (pushAction) {
    pushAction({
      type: "WIDGET_ADD",
      payload: {
        widgetId: newWidget.id,
        addedWidget: {
          id: newWidget.id,
          space_id: newWidget.space_id,
          widget_type_id: newWidget.widget_type_id || newWidget.widget_type?.id,
          config: { ...newWidget.config },
          layout: { ...newWidget.layout },
          position: newWidget.layout?.desktop?.position || 0,
          // Store additional metadata for database operations
          widget_type: newWidget.widget_type,
          created_at: new Date().toISOString(),
          last_autosave_at: new Date().toISOString(),
        },
      },
      previousState,
      newState,
    });
  }

  // Note: Database save should be handled by the calling component
  // since widget additions typically involve edge function calls
  console.log(
    `✅ [WidgetUtils] Widget ${newWidget.id} added to history with complete database info`
  );
};

/**
 * Creates a debounced function for handling content changes with history tracking
 * @param {Function} setSpaceData - State setter for space data
 * @param {Function} pushAction - Function to add action to history
 * @param {Function} queueAction - Function to queue updates for saving
 * @param {number} delay - Debounce delay in milliseconds (default: 1000ms)
 * @returns {Function} - Debounced content update function
 */
export const createDebouncedContentUpdate = (
  setSpaceData,
  pushAction,
  queueAction,
  delay = 1000
) => {
  return debounce((widgetId, changes, actionType = "CONTENT_CHANGE") => {
    console.log(
      `📝 [DebouncedContent] Processing ${actionType} for widget ${widgetId}`
    );

    // Get current state before update for history tracking
    let previousState = null;
    setSpaceData((prevData) => {
      previousState = {
        widgets: prevData.widgets.map((w) => ({
          ...w,
          layout: { ...w.layout },
          config: { ...w.config },
        })),
      };
      return prevData;
    });

    let newState = null;

    setSpaceData((prevData) => {
      const updatedWidgets = prevData.widgets.map((widget) => {
        if (widget.id === widgetId) {
          return {
            ...widget,
            config: changes.config || widget.config,
            layout: changes.layout || widget.layout,
          };
        }
        return widget;
      });

      const newData = {
        ...prevData,
        widgets: updatedWidgets,
      };

      // Capture the new state for history
      newState = {
        widgets: updatedWidgets.map((w) => ({
          ...w,
          layout: { ...w.layout },
          config: { ...w.config },
        })),
      };

      return newData;
    });

    // Record in history
    if (pushAction) {
      pushAction({
        type: actionType,
        payload: {
          widgetId,
          changes,
        },
        previousState,
        newState,
      });
    }

    // Queue for database save
    if (queueAction) {
      queueAction(
        {
          widgets: {
            [widgetId]: {
              ...changes,
              id: widgetId,
              last_autosave_at: new Date().toISOString(),
            },
          },
        },
        actionType,
        null,
        false, // Don't skip history since we handled it above
        true // Always trigger autosave for content changes
      );
    }

    console.log(
      `✅ [DebouncedContent] ${actionType} completed for widget ${widgetId}`
    );
  }, delay);
};
